import {useMemo, useState} from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type {MarkdownArtifactPanelData} from '@reachinsight-app/shared/src/components/R3Chat/types'
import {MarkdownArtifactService} from './MarkdownArtifactService'

export function MarkdownArtifactPanel({state, service, onChange}: {
  state: MarkdownArtifactPanelData
  service: MarkdownArtifactService
  onChange: (state: MarkdownArtifactPanelData) => void
}) {
  const [reviewTab, setReviewTab] = useState<'before' | 'after'>('after')
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historical, setHistorical] = useState<{revision: number; previewHtml: string} | null>(null)
  const [comparison, setComparison] = useState<{addedLines?: number; removedLines?: number} | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const preview = historical?.previewHtml ?? (state.pendingReview
    ? reviewTab === 'before' ? state.pendingReview.beforeHtml : state.pendingReview.afterHtml
    : state.previewHtml)
  const iframeDocument = useMemo(() => `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: https:; style-src 'unsafe-inline'"><style>body{font-family:system-ui,sans-serif;line-height:1.55;color:#292722;padding:24px;max-width:760px;margin:auto}pre,code{white-space:pre-wrap;overflow-wrap:anywhere}img{max-width:100%}</style></head><body>${preview}</body></html>`, [preview])

  const act = async (label: string, action: () => Promise<MarkdownArtifactPanelData>) => {
    setBusy(label); setError(null)
    try { onChange(await action()); setHistorical(null) }
    catch (value) { setError(value instanceof Error ? value.message : 'Action failed') }
    finally { setBusy(null) }
  }

  const download = async () => {
    setBusy('export'); setError(null)
    try {
      const format = state.format === 'markdown' ? 'markdown' : 'text'
      const {blob, fileName} = await service.exportExact(state.sessionId, historical?.revision ?? state.revision, format)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = fileName; anchor.click()
      URL.revokeObjectURL(url)
    } catch (value) { setError(value instanceof Error ? value.message : 'Export failed') }
    finally { setBusy(null) }
  }

  return (
    <Box sx={{height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper'}}>
      <Box sx={{px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1}}>
        <Box sx={{minWidth: 0, flex: 1}}>
          <Typography noWrap sx={{fontWeight: 650, fontSize: '0.95rem'}}>{state.title}</Typography>
          <Typography sx={{fontSize: '0.7rem', color: 'text.secondary'}}>{state.format === 'markdown' ? 'Markdown' : 'Plain text'} · Version {state.revision + 1}</Typography>
        </Box>
        {state.pendingReview && <Chip size="small" color="warning" label="Review required" />}
        <Button size="small" onClick={() => setHistoryOpen(value => !value)}>History</Button>
        <Button size="small" disabled={busy !== null} onClick={() => {void download()}}>Export</Button>
        <Tooltip title={state.release.message}><span><Button size="small" disabled>Share</Button></span></Tooltip>
      </Box>
      <Divider />
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {state.pendingReview && (
        <Box sx={{px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider'}}>
          <Typography sx={{fontSize: '0.8rem', fontWeight: 600}}>{state.pendingReview.summary}</Typography>
          <Tabs value={reviewTab} onChange={(_event, value: 'before' | 'after') => setReviewTab(value)} sx={{minHeight: 34}}>
            <Tab value="before" label="Before" sx={{minHeight: 34, py: 0}} />
            <Tab value="after" label="After" sx={{minHeight: 34, py: 0}} />
          </Tabs>
          <Box sx={{display: 'flex', gap: 1, mt: 1}}>
            <Button variant="contained" size="small" disabled={busy !== null} onClick={() => void act('approve', () => service.approve(state.sessionId, state.pendingReview!.changeSetId, state.pendingReview!.summary))}>Approve</Button>
            <Button variant="outlined" size="small" disabled={busy !== null} onClick={() => void act('refine', () => service.refine(state.sessionId, state.pendingReview!.changeSetId))}>Refine in chat</Button>
            <Button color="error" size="small" disabled={busy !== null} onClick={() => void act('reject', () => service.reject(state.sessionId, state.pendingReview!.changeSetId, 'Rejected in review panel'))}>Reject</Button>
          </Box>
        </Box>
      )}
      <Box sx={{flex: 1, minHeight: 0, display: 'flex', position: 'relative'}}>
        {historyOpen && (
          <Box sx={{width: 250, flexShrink: 0, overflowY: 'auto', borderRight: '1px solid', borderColor: 'divider'}}>
            <List dense disablePadding>
              {(state.revisions ?? []).map(revision => (
                <ListItemButton key={revision.revision} selected={(historical?.revision ?? state.revision) === revision.revision} onClick={async () => {
                  setBusy('history'); setError(null)
                  try { setHistorical(await service.readRevision(state.sessionId, revision.revision)) }
                  catch (value) { setError(value instanceof Error ? value.message : 'Version could not be loaded') }
                  finally { setBusy(null) }
                }}>
                  <ListItemText primary={`Version ${revision.revision + 1}`} secondary={revision.summary} primaryTypographyProps={{fontSize: '0.78rem'}} secondaryTypographyProps={{fontSize: '0.68rem'}} />
                </ListItemButton>
              ))}
            </List>
            {historical && historical.revision !== state.revision && (
              <Box sx={{p: 1, display: 'grid', gap: 1}}>
                <Button size="small" variant="outlined" disabled={busy !== null} onClick={() => void act('restore', () => service.restore(state.sessionId, historical.revision, state.revision))}>Restore this version</Button>
                <Button size="small" disabled={busy !== null} onClick={async () => {
                  setBusy('compare'); setError(null)
                  try { setComparison((await service.compare(state.sessionId, historical.revision, state.revision)).difference as {addedLines?: number; removedLines?: number}) }
                  catch (value) { setError(value instanceof Error ? value.message : 'Versions could not be compared') }
                  finally { setBusy(null) }
                }}>Compare with current</Button>
                {comparison && <Typography sx={{fontSize: '0.7rem', color: 'text.secondary'}}>+{comparison.addedLines ?? 0} / −{comparison.removedLines ?? 0} lines</Typography>}
                <Button size="small" onClick={() => setHistorical(null)}>Return to current</Button>
              </Box>
            )}
            {state.revision > 0 && !historical && <Button fullWidth size="small" disabled={busy !== null} onClick={() => void act('undo', () => service.undo(state.sessionId, state.revision))}>Undo latest version</Button>}
          </Box>
        )}
        <Box sx={{flex: 1, minWidth: 0, position: 'relative'}}>
          {busy && <CircularProgress size={22} sx={{position: 'absolute', zIndex: 1, top: 12, right: 12}} />}
          <Box component="iframe" title={`${state.title} preview`} sandbox="" srcDoc={iframeDocument} sx={{border: 0, width: '100%', height: '100%', bgcolor: '#fff'}} />
        </Box>
      </Box>
    </Box>
  )
}
